import React, { useCallback, useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";

import Portal from "../../common/components/Portal";
import Loader from "../../common/components/Loader";
import { ExecutiveSummaryBandsPanel } from "./ExecutiveSummaryBandsPanel";
import {
    buildExecutiveSummaryLogExpandedKeys,
    prepareExecutiveSummaryLogCompareData
} from "./executiveSummaryLogCompare";
import { ExecutiveSummaryBulkBandsToolbar } from "./ExecutiveSummaryBulkBandsToolbar";
import "./ExecutiveSummaryLogCompareModal.css";
import "./ExecutiveSummaryBandsPanel.css";

function ComparePanel({
    title,
    headerClass,
    bands,
    expandedBandKeys,
    onToggleBand,
    onExpandBands,
    onCollapseBands,
    buildingImages,
    hiddenOnMobile,
    tooltipIdPrefix,
    renderBandLeadingAddon,
    toolbarAddon
}) {
    const panelClasses = [
        "exec-sum-log-compare-modal__panel",
        hiddenOnMobile ? "exec-sum-log-compare-modal__panel--hidden-mobile" : ""
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={panelClasses}>
            <div className={`exec-sum-log-compare-modal__panel-header ${headerClass}`}>{title}</div>
            <div className="exec-sum-log-compare-modal__panel-body">
                {bands.length ? (
                    <>
                        <div className="exec-sum-log-compare-modal__panel-toolbar-row">
                            <ExecutiveSummaryBulkBandsToolbar
                                bands={bands}
                                expandedBandKeys={expandedBandKeys}
                                onExpandBands={onExpandBands}
                                onCollapseBands={onCollapseBands}
                                tooltipIdPrefix={tooltipIdPrefix}
                                wrapperClassName="exec-sum-log-compare-modal__panel-toolbar"
                            />
                            {toolbarAddon}
                        </div>
                        <div className="exec-sum-log-compare-modal__panel-scroll-host">
                            <ExecutiveSummaryBandsPanel
                                bands={bands}
                                buildingImages={buildingImages}
                                expandedBandKeys={expandedBandKeys}
                                onToggleBand={onToggleBand}
                                onExpandBands={onExpandBands}
                                onCollapseBands={onCollapseBands}
                                editable={false}
                                variant="logCompare"
                                tooltipIdPrefix={tooltipIdPrefix}
                                renderBandLeadingAddon={renderBandLeadingAddon}
                            />
                        </div>
                    </>
                ) : (
                    <div className="exec-sum-log-compare-modal__empty">No changed bands</div>
                )}
            </div>
        </div>
    );
}

const MODIFIED_CHIPS_SHOWN = 4;

function formatBandList(labels) {
    return labels.filter(Boolean).join(", ");
}

function DiffSummaryChips({ summary }) {
    if (!summary) return null;
    const { added, removed, modified, reordered = [], topLevelOrderChanged = false } = summary;
    const hasChanges =
        added.length > 0 ||
        removed.length > 0 ||
        modified.length > 0 ||
        reordered.length > 0 ||
        topLevelOrderChanged;

    if (!hasChanges) {
        return (
            <div className="exec-sum-log-compare-modal__summary">
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--modified">
                    No band changes detected
                </span>
            </div>
        );
    }

    return (
        <div className="exec-sum-log-compare-modal__summary">
            {topLevelOrderChanged ? (
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--reordered">
                    Band order changed
                </span>
            ) : null}
            {added.length > 0 ? (
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--added">
                    {added.length === 1 ? `Added: ${added[0]}` : `${added.length} bands added: ${formatBandList(added)}`}
                </span>
            ) : null}
            {removed.length > 0 ? (
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--removed">
                    {removed.length === 1
                        ? `Removed: ${removed[0]}`
                        : `${removed.length} bands removed: ${formatBandList(removed)}`}
                </span>
            ) : null}
            {reordered.length > 0 ? (
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--reordered">
                    {reordered.length === 1
                        ? `Child order changed: ${reordered[0]}`
                        : `Child order changed: ${formatBandList(reordered)}`}
                </span>
            ) : null}
            {modified.slice(0, MODIFIED_CHIPS_SHOWN).map(entry => (
                <span
                    key={entry}
                    className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--modified"
                >
                    {`Modified: ${entry}`}
                </span>
            ))}
            {modified.length > MODIFIED_CHIPS_SHOWN ? (
                <span className="exec-sum-log-compare-modal__chip exec-sum-log-compare-modal__chip--modified">
                    {`+${modified.length - MODIFIED_CHIPS_SHOWN} more modified`}
                </span>
            ) : null}
        </div>
    );
}

export default function ExecutiveSummaryLogCompareModal({
    show,
    onClose,
    changesetValues,
    user,
    createdAt,
    buildingImages = [],
    heading = "Executive Summary changes",
    beforeTitle = "Previous",
    afterTitle = "New",
    footerActions,
    renderAfterBandLeadingAddon,
    afterToolbarAddon
}) {
    const [activeTab, setActiveTab] = useState("before");
    const [beforeExpanded, setBeforeExpanded] = useState({});
    const [afterExpanded, setAfterExpanded] = useState({});
    const [parsed, setParsed] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show || !changesetValues) {
            setParsed(null);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setParsed(null);
        const timer = window.setTimeout(() => {
            try {
                const result = prepareExecutiveSummaryLogCompareData(changesetValues);
                setParsed({
                    beforeBands: result.beforeBands,
                    afterBands: result.afterBands,
                    summary: result.summary,
                    error: null
                });
                setBeforeExpanded(buildExecutiveSummaryLogExpandedKeys(result.beforeBands));
                setAfterExpanded(buildExecutiveSummaryLogExpandedKeys(result.afterBands));
                setActiveTab("before");
            } catch (e) {
                setParsed({
                    beforeBands: [],
                    afterBands: [],
                    summary: null,
                    error: e?.message || "Could not load executive summary for this log entry"
                });
            }
            setLoading(false);
        }, 0);

        return () => window.clearTimeout(timer);
    }, [show, changesetValues]);

    useEffect(() => {
        if (!show) return;
        ReactTooltip.rebuild();
    }, [show, parsed, activeTab, loading]);

    const toggleBeforeBand = useCallback(rowKey => {
        setBeforeExpanded(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
    }, []);

    const toggleAfterBand = useCallback(rowKey => {
        setAfterExpanded(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
    }, []);

    const expandBeforeBands = useCallback(rowKeys => {
        setBeforeExpanded(prev => {
            const next = { ...prev };
            rowKeys.forEach(rowKey => {
                next[rowKey] = true;
            });
            return next;
        });
    }, []);

    const collapseBeforeBands = useCallback(rowKeys => {
        setBeforeExpanded(prev => {
            const next = { ...prev };
            rowKeys.forEach(rowKey => {
                next[rowKey] = false;
            });
            return next;
        });
    }, []);

    const expandAfterBands = useCallback(rowKeys => {
        setAfterExpanded(prev => {
            const next = { ...prev };
            rowKeys.forEach(rowKey => {
                next[rowKey] = true;
            });
            return next;
        });
    }, []);

    const collapseAfterBands = useCallback(rowKeys => {
        setAfterExpanded(prev => {
            const next = { ...prev };
            rowKeys.forEach(rowKey => {
                next[rowKey] = false;
            });
            return next;
        });
    }, []);

    if (!show) return null;

    const modalContent = (
        <div
            className="modal modal-region modal-view inbox-modal exec-sum-log-compare-modal-wrap"
            style={{ display: "block" }}
            tabIndex={-1}
            role="dialog"
            aria-labelledby="exec-sum-log-compare-title"
            aria-modal="true"
        >
            <div className="modal-dialog exec-sum-log-compare-modal" role="document">
                <div className="modal-content">
                    <div className="exec-sum-log-compare-modal__header">
                            <div className="exec-sum-log-compare-modal__icon-wrap" aria-hidden="true">
                                <i className="fas fa-exchange-alt" />
                            </div>
                            <div className="exec-sum-log-compare-modal__title-wrap">
                                <h2 id="exec-sum-log-compare-title" className="exec-sum-log-compare-modal__title">
                                    {heading}
                                </h2>
                                <p className="exec-sum-log-compare-modal__meta">
                                    {user ? <span>{user}</span> : null}
                                    {user && createdAt ? <span> · </span> : null}
                                    {createdAt ? <span>{createdAt}</span> : null}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="exec-sum-log-compare-modal__close"
                                aria-label="Close"
                                data-tip="Close"
                                data-for="exec-sum-log-compare-actions"
                                onClick={onClose}
                            >
                                <i className="fas fa-times" aria-hidden="true" />
                            </button>
                        </div>

                        <div className="exec-sum-log-compare-modal__body">
                            {loading ? (
                                <div className="exec-sum-log-compare-modal__loading">
                                    <Loader />
                                    <span>Loading changes…</span>
                                </div>
                            ) : parsed?.error ? (
                                <div className="exec-sum-log-compare-modal__error" role="alert">
                                    {parsed.error}
                                </div>
                            ) : (
                                <>
                                    <DiffSummaryChips summary={parsed?.summary} />
                                    {parsed?.summary?.mixedOrderAndContent ? (
                                        <p className="exec-sum-log-compare-modal__order-note">
                                            Top-level band order also changed — showing all bands so you can compare
                                            positions.
                                        </p>
                                    ) : null}

                                    <div className="exec-sum-log-compare-modal__tabs" role="tablist" aria-label="Compare views">
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={activeTab === "before"}
                                            className={`exec-sum-log-compare-modal__tab exec-sum-log-compare-modal__tab--before${
                                                activeTab === "before" ? " exec-sum-log-compare-modal__tab--active" : ""
                                            }`}
                                            onClick={() => setActiveTab("before")}
                                        >
                                            {beforeTitle}
                                        </button>
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={activeTab === "after"}
                                            className={`exec-sum-log-compare-modal__tab exec-sum-log-compare-modal__tab--after${
                                                activeTab === "after" ? " exec-sum-log-compare-modal__tab--active" : ""
                                            }`}
                                            onClick={() => setActiveTab("after")}
                                        >
                                            {afterTitle}
                                        </button>
                                    </div>

                                    <div className="exec-sum-log-compare-modal__panels">
                                        <ComparePanel
                                            title={beforeTitle}
                                            headerClass="exec-sum-log-compare-modal__panel-header--before"
                                            bands={parsed?.beforeBands ?? []}
                                            expandedBandKeys={beforeExpanded}
                                            onToggleBand={toggleBeforeBand}
                                            onExpandBands={expandBeforeBands}
                                            onCollapseBands={collapseBeforeBands}
                                            buildingImages={buildingImages}
                                            hiddenOnMobile={activeTab !== "before"}
                                            tooltipIdPrefix="exec_sum_log_before"
                                        />
                                        <ComparePanel
                                            title={afterTitle}
                                            headerClass="exec-sum-log-compare-modal__panel-header--after"
                                            bands={parsed?.afterBands ?? []}
                                            expandedBandKeys={afterExpanded}
                                            onToggleBand={toggleAfterBand}
                                            onExpandBands={expandAfterBands}
                                            onCollapseBands={collapseAfterBands}
                                            buildingImages={buildingImages}
                                            hiddenOnMobile={activeTab !== "after"}
                                            tooltipIdPrefix="exec_sum_log_after"
                                            renderBandLeadingAddon={renderAfterBandLeadingAddon}
                                            toolbarAddon={afterToolbarAddon}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="exec-sum-log-compare-modal__footer">
                            {footerActions ?? (
                                <button
                                    type="button"
                                    className="exec-sum-log-compare-modal__btn"
                                    data-tip="Close comparison"
                                    data-for="exec-sum-log-compare-actions"
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            <ReactTooltip
                id="exec-sum-log-compare-actions"
                effect="solid"
                place="top"
                backgroundColor="#007bff"
            />
        </div>
    );

    return <Portal body={modalContent} onCancel={onClose} />;
}
